/**
 * The single definition of "which action does the user land on after resolving
 * one?" for both AI Actions shells.
 *
 * The rule: advance to the neighbour BELOW the resolved action in display
 * order; when the resolved action was the last one, fall back to the neighbour
 * ABOVE. Returns null when nothing survives to advance to.
 *
 * This lived in three places with two different algorithms before — twice
 * inline in ProposalListPanel (approve/dismiss) and once in
 * ProposalListSidebar's bulk resolution, which also bypassed the sidebar's own
 * `getNextAction` ref method implementing the same rule. Keeping one copy is
 * the point: the next change to "where does approving send you" has exactly
 * one place to land.
 *
 * `removedIds` is a set so the rail's bulk path (which resolves a whole tier at
 * once) and the list's single-action path share one implementation — the single
 * path passes a one-element set. Callers must pass ids in DISPLAY order and
 * must capture them BEFORE awaiting the resolve: the list cache is mutated
 * during the await, so a post-await lookup would miss the removed row.
 */
export function getNextActionId(
  flattenedActionIds: readonly number[],
  currentActionId: number,
  removedIds: ReadonlySet<number>
): number | null {
  const currentIndex = flattenedActionIds.indexOf(currentActionId);
  if (currentIndex === -1) {
    return null;
  }

  // Neighbour below: the first surviving action after the resolved one.
  for (let i = currentIndex + 1; i < flattenedActionIds.length; i++) {
    const id = flattenedActionIds[i];
    if (!removedIds.has(id)) {
      return id;
    }
  }

  // Nothing below survived — fall back to the last surviving action above.
  for (let i = currentIndex - 1; i >= 0; i--) {
    const id = flattenedActionIds[i];
    if (!removedIds.has(id)) {
      return id;
    }
  }

  return null;
}
